import { formatPrice } from '@getjust/commons';
import { FeatureFlag } from '@getjust/constants';
import { Cart } from '@getjust/leaf';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { StackProps } from '@chakra-ui/react';

import Coupon from '$components/SvgLogos/Coupon';
import { useAddDiscount, useDeleteDiscount } from '$hooks/mutations';
import { useCart, useShop } from '$hooks/queries';
import { LoyaltyRewards } from '$src/components/LoyaltyRewards';
import { QuantitySheet } from '$src/components/QuantitySheet';
import { useIsDesktop } from '$src/hooks/client/useIsDesktop';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useTrackTransition } from '$src/hooks/client/useTrackTransition';
import { useClearGiftCards } from '$src/hooks/mutations/useClearGiftCards';
import { useUpdateLineItems } from '$src/hooks/mutations/useUpdateLineItems';
import { useCartLineItemsMutatingState } from '$src/hooks/state/useCartLineItemsMutatingState';
import { useCheckoutFeaturesAtom } from '$src/hooks/state/useCheckoutFeaturesAtom';
import { useCorrectionsAtom } from '$src/hooks/state/useCorrectionsAtom';
import { useRecommendedDiscountsAtom } from '$src/hooks/state/useRecommendedDiscountsAtom';

interface CartDetailProps extends StackProps {
  showTotal?: boolean;
  showEmailOnly?: boolean;
  /**
   * Optional slot rendered just after the Total row, before the discount tags
   * and the product list. Used on mobile to inject the native discount input
   * at a more behaviorally-natural position (between totals and items).
   */
  extraBeforeDiscounts?: React.ReactNode;
  /**
   * Force-hide the per-line quantity selector regardless of the shop feature
   * flag. Used by the single-product hero, where quantity is edited through a
   * dedicated bottom sheet — showing a second selector here would be redundant.
   */
  isQuantityHidden?: boolean;
  /**
   * Force-hide Leaf's own discount input group regardless of the shop feature
   * flag or viewport. Used by the desktop cart sidebar, where the rev-8
   * discount section is injected via `extraBeforeDiscounts` and Leaf's input
   * would be a duplicate. Already-applied discount chips are unaffected.
   */
  hideDiscountInput?: boolean;
  /**
   * Suppress Leaf's product line-item list (and its leading divider). Used by
   * the desktop cart sidebar's single-product hero, where the product is shown
   * as a large centered showcase above this Cart — listing it again here would
   * duplicate it. Totals + discounts are unaffected. Default false.
   */
  hideProducts?: boolean;
  /**
   * Density pass for the desktop cart sidebar. Forwarded straight to Leaf's
   * `Cart`, which scales the product rows, totals, Total row, and quantity
   * selector ~×0.85 for a ~15%-denser look. Default false leaves every other
   * consumer (mobile, Footer, EmailOnly) unchanged.
   */
  dense?: boolean;
}

const CartDetail = ({
  showTotal = true,
  showEmailOnly = false,
  extraBeforeDiscounts,
  isQuantityHidden = false,
  hideDiscountInput = false,
  hideProducts = false,
  dense = false,
  ...props
}: CartDetailProps) => {
  const { mutateAsync: updateLineItems } = useUpdateLineItems();
  const { canUseCheckoutFeature } = useCheckoutFeaturesAtom();
  const { error: discountError, addDiscount, status } = useAddDiscount();
  const { mutateAsync: deleteDiscount } = useDeleteDiscount();
  const { mutateAsync: clearGiftCards } = useClearGiftCards();
  const { t } = useTranslation('common');
  const { data: cart } = useCart();
  const { track } = useTrackAnalytics();
  const { corrections, initCorrections, clearCorrections } = useCorrectionsAtom();
  const { isMobile } = useIsDesktop();
  const [discountValue, setDiscountValue] = useState('');
  const { data: shop } = useShop();
  const { recommendedDiscountsAtom: recommendedDiscount } = useRecommendedDiscountsAtom();
  const { isItemMutating, pendingAdditions, isCartMutating } = useCartLineItemsMutatingState();

  const onDiscountAddClick = useCallback(
    async (code: string) => {
      track(AnalyticsEventName.DISCOUNT_CODE_SUBMITTED, { code });
      await addDiscount({
        code: code.trim(),
      });
    },
    [addDiscount, track],
  );

  const onDeleteDiscount = useCallback(
    (code: string) => {
      track(AnalyticsEventName.DISCOUNT_CODE_DELETE_SUBMITTED, { code });
      return deleteDiscount({ code });
    },
    [deleteDiscount, track],
  );

  const getLeftQuantityTitle = useCallback(
    (quantityAvailable: number | undefined, isAvailable: boolean): string => {
      if (!quantityAvailable && !isAvailable) return t('errors.out_of_stock');
      if (!quantityAvailable && isAvailable) return '';
      if (quantityAvailable === 1) return t('commons.leftQuantity', { count: quantityAvailable });
      return t('commons.leftQuantity_plurials', { count: quantityAvailable });
    },
    [t],
  );

  // Shorter low-stock copy ("Plus que X en stock") used by the mobile badge.
  const getLeftQuantityShortTitle = useCallback(
    (quantityAvailable: number | undefined): string =>
      quantityAvailable ? t('commons.leftQuantityShort', { count: quantityAvailable }) : '',
    [t],
  );

  const onGiftCardRemove = useCallback(() => clearGiftCards(), [clearGiftCards]);

  const onUpdateItem = useCallback(
    async ({
      newQuantity,
      id,
      productId,
      variantId,
      customizationId,
    }: {
      id: string;
      productId: string | undefined;
      variantId: string | undefined;
      newQuantity: number;
      customizationId: string | undefined;
    }): Promise<number> => {
      const res = await updateLineItems({
        lineItems: [
          {
            id,
            productId,
            variantId,
            quantity: newQuantity,
            customizationId,
          },
        ],
      });

      if (!res.data.corrections?.length) {
        clearCorrections();
      } else {
        initCorrections(res.data.corrections);
      }

      // Get the actual applied quantity from the response
      const updatedLineItem = res.data.lineItems?.find((item) => item.id === id);
      return updatedLineItem?.quantity ?? newQuantity;
    },
    [updateLineItems, clearCorrections, initCorrections],
  );

  // Quantity bottom-sheet (shared with the mobile hero flow). A line's "Qté N"
  // chip opens it; "Update" / "Remove" commit through the same mutation.
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isUpdatingQty, setIsUpdatingQty] = useState(false);
  const editingItem = cart?.lineItems?.find((lineItem) => lineItem.id === editingItemId) ?? null;
  const editingMaxQuantity =
    typeof editingItem?.quantityAvailable === 'number' && editingItem.quantityAvailable > 0
      ? editingItem.quantityAvailable
      : undefined;

  const onSheetUpdate = useCallback(
    async (newQuantity: number) => {
      if (!editingItem) return;
      setIsUpdatingQty(true);
      try {
        await onUpdateItem({
          newQuantity,
          id: editingItem.id,
          productId: editingItem.productId,
          variantId: editingItem.variantId,
          customizationId: editingItem.customizationId,
        });
        setEditingItemId(null);
      } catch {
        // Error toast surfaces from the mutation's onError.
      } finally {
        setIsUpdatingQty(false);
      }
    },
    [editingItem, onUpdateItem],
  );

  useEffect(() => {
    if (cart?.corrections?.length) {
      initCorrections(cart?.corrections);
    }
    return () => {
      clearCorrections();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shippingMessage = useMemo(() => {
    if (
      cart?.shipping?.selected &&
      cart?.shipping?.address &&
      cart?.shipping?.address?.firstName !== 'Just' && // JUST Users in Prestashop
      cart?.shipping?.address?.lastName !== 'Anonymous' && // mandatory to have estimated
      cart?.shipping?.address?.address1 !== '142 AVENUE LEDRU ROLLIN' // shipping in prestashop
    ) {
      return cart?.totalShipping === 0
        ? t('notAuthed.freeShipping')
        : `${formatPrice(cart?.totalShipping, {
            currency: cart?.currency ? cart.currency : 'EUR',
          })}`;
    }

    if (showEmailOnly) {
      return t('commons.next_step_calculating');
    } else {
      return t('commons.enter_address_to_calculate');
    }
  }, [cart, t, showEmailOnly]);

  const totalMessage = useMemo(() => {
    return {
      label: t('commons.total'),
      value: formatPrice(cart?.totalAmount ?? 0, { currency: cart?.currency ?? 'EUR' }),
    };
  }, [cart, t]);

  const recommendedDiscountCodes = useMemo(() => {
    return recommendedDiscount?.map((el) => el.code) ?? [];
  }, [recommendedDiscount]);

  // Recommended discounts shaped for the UI: each carries a pre-formatted
  // "Save <amount>" label (the savings value is already known client-side) so
  // the card can frame it as a real, quantified saving rather than a bare code.
  const recommendedDiscountsForUi = useMemo(() => {
    const currency = cart?.currency ?? 'EUR';
    const shopName = shop?.name;
    return (
      recommendedDiscount?.map((el) => {
        if (!(el.totalDiscount > 0)) return { code: el.code };
        const amount = formatPrice(el.totalDiscount, { currency });
        return {
          code: el.code,
          saveLabel: t('commons.recommended_discount_save', { amount }),
          // Shown once applied: the card transforms into a "you saved X" message,
          // credited to the shop when we know its name (else a neutral phrasing).
          appliedLabel: shopName
            ? t('commons.recommended_discount_applied', { shop: shopName, amount })
            : t('commons.recommended_discount_applied_generic', { amount }),
        };
      }) ?? []
    );
  }, [recommendedDiscount, cart?.currency, shop?.name, t]);

  useTrackTransition({
    value: recommendedDiscountCodes?.length > 0 && recommendedDiscountCodes?.length,
    trueEventName: AnalyticsEventName.FINDER_TRIGGERED,
    payload: {
      'Nb codes': recommendedDiscount?.length,
      codes: recommendedDiscount?.map((el) => el.code),
    },
  });

  // On mobile the discount input is rendered natively by MobileCartSummary
  // (matches the new design tokens). Hide Leaf's heavier input here.
  const isDiscountHidden =
    hideDiscountInput || canUseCheckoutFeature(FeatureFlag.HIDE_DISCOUNT_INPUT) || isMobile;

  const showBundleItemsLabel = useCallback(
    (count: number) =>
      t(count > 1 ? 'commons.bundle_show_items_plurials' : 'commons.bundle_show_items', { count }),
    [t],
  );
  const hideBundleItemsLabel = useCallback(
    (count: number) =>
      t(count > 1 ? 'commons.bundle_hide_items_plurials' : 'commons.bundle_hide_items', { count }),
    [t],
  );

  return (
    <>
    <Cart
      discountCodeQuestion={t('commons.discount_code_question')}
      addDiscountLabel={t('buttons.add')}
      discountCodeDrawerTitle={t('commons.discount_code_title')}
      showEmailOnly={showEmailOnly}
      isDiscountHidden={isDiscountHidden}
      onDiscountAddClick={onDiscountAddClick}
      onDiscountRemove={onDeleteDiscount}
      onGiftCardRemove={onGiftCardRemove}
      discountApplyLabel={t('buttons.add_discount')}
      discountInputLabel={t('commons.discount_code')}
      discountFinderLabel={t('commons.recommended_discount_title')}
      recommendedDiscounts={recommendedDiscountsForUi}
      discountErrorLabel={
        status === 'error'
          ? (discountError.message ??
            (t('errors.commons.unknown_discount', { code: discountValue }) as string))
          : undefined
      }
      total={showTotal ? totalMessage : { label: '', value: '' }}
      subTotal={{
        // On mobile (B2C surface) "HT" reads as invoicing noise; collapse to
        // the simpler label regardless of taxIncluded.
        label:
          isMobile
            ? t('commons.subtotal')
            : !cart?.taxIncluded
              ? t('commons.subtotalHT')
              : t('commons.subtotal'),
        value: formatPrice((cart?.subTotal ?? 0) + (cart?.totalSale ?? 0), {
          currency: cart?.currency ?? 'EUR',
        }),
      }}
      shipping={{
        label: t('commons.shipping_method'),
        value: shippingMessage,
      }}
      tax={
        cart?.collectTax && !cart?.taxIncluded
          ? {
              value: cart?.taxReady
                ? formatPrice(cart?.totalTax ?? 0, {
                    currency: cart?.currency ?? 'EUR',
                  })
                : t('commons.calculating'),
              label: t('commons.tax'),
            }
          : undefined
      }
      products={(cart?.lineItems ?? []).map((lineItem) => ({
        ...lineItem,
        price: formatPrice(lineItem?.totalAmount, { currency: cart?.currency ?? 'EUR' }),
        variant: lineItem?.variants,
        image: lineItem?.imageUrl ?? '',
      }))}
      totalSale={
        cart?.totalSale
          ? {
              label: t('commons.sale'),
              value: formatPrice(cart?.totalSale ?? 0, {
                currency: cart?.currency ?? 'EUR',
              }),
            }
          : undefined
      }
      totalDiscount={
        cart?.totalDiscount
          ? {
              label: t('commons.discount'),
              value: formatPrice(cart?.totalDiscount ?? 0, {
                currency: cart?.currency ?? 'EUR',
              }),
            }
          : undefined
      }
      totalDuties={
        cart?.totalDuties
          ? {
              label: t('commons.duties'),
              value: formatPrice(cart?.totalDuties ?? 0, {
                currency: cart?.currency ?? 'EUR',
              }),
            }
          : undefined
      }
      discounts={cart?.discounts ?? []}
      giftCards={cart?.giftCards ?? []}
      totalGiftCard={
        cart?.totalGiftCard
          ? {
              label: t('commons.gift_card'),
              value: formatPrice(cart?.totalGiftCard ?? 0, {
                currency: cart?.currency ?? 'EUR',
              }),
            }
          : undefined
      }
      isQuantityDisabled={isQuantityHidden || !shop?.features?.includes('quantity_selector') || showEmailOnly}
      isStockDisabled={!shop?.features?.includes('product_stock_indicator')}
      currency={cart?.currency ?? 'EUR'}
      shouldFocusDiscountField={false}
      updateQuantity={onUpdateItem}
      taxIncludedTitle={cart?.collectTax ? t('commons.taxIncludedTitle') : undefined}
      leftQuantityTitles={
        cart?.lineItems?.map((lineItem) =>
          getLeftQuantityTitle(lineItem.quantityAvailable, lineItem.isAvailable),
        ) ?? ['']
      }
      leftQuantityShortTitles={
        cart?.lineItems?.map((lineItem) => getLeftQuantityShortTitle(lineItem.quantityAvailable)) ?? ['']
      }
      quantityLabel={t('commons.qty')}
      onEditQuantity={(lineItemId) => setEditingItemId(lineItemId)}
      corrections={corrections ?? []}
      Icon={Coupon}
      availabilityMessage={corrections?.length ? t('errors.availability_warning') : ''}
      updatedCartInformation={corrections?.length ? t('info.cart_updated') : ''}
      outOfStockMessage={corrections?.length ? t('errors.out_of_stock') : ''}
      isMobile={isMobile}
      setDiscountValue={setDiscountValue}
      showBundleItemsLabel={showBundleItemsLabel}
      hideBundleItemsLabel={hideBundleItemsLabel}
      renderBeforeDiscounts={
        <>
          {/* Mobile keeps loyalty inside the cart card. On desktop it's lifted
              out into its own card below the cart (see Layout). */}
          {isMobile && <LoyaltyRewards />}
          {extraBeforeDiscounts}
        </>
      }
      isItemLoading={isItemMutating}
      isAnyItemLoading={!!isCartMutating}
      pendingProducts={pendingAdditions}
      hideProducts={hideProducts}
      dense={dense}
      {...props}
    />
      <QuantitySheet
        isOpen={Boolean(editingItem)}
        onClose={() => setEditingItemId(null)}
        imageUrl={editingItem?.imageUrl}
        productName={editingItem?.name ?? ''}
        quantity={editingItem?.quantity ?? 1}
        maxQuantity={editingMaxQuantity}
        isPending={isUpdatingQty}
        onUpdate={onSheetUpdate}
        onRemove={(cart?.lineItems?.length ?? 0) > 1 ? () => void onSheetUpdate(0) : undefined}
      />
    </>
  );
};

export default CartDetail;
